import { Context } from '../..';
import { AIAgentType } from '../../../enums/ai-agent-type';
import * as base from '../base';

const CONTROLLER = 'agent';

export interface AgentSchema {
  requestSchema: any;
  responseSchema: any;
  displayName: string;
  description: string;
}

/**
 * Executes an agent request with type safety using TypeScript generics
 * @param agentType The type of agent
 * @param parameters The request parameters
 * @param companySlug The company slug
 * @returns The typed response
 */
export async function executeTypedAgentRequest<TRequest, TResponse>(agentType: AIAgentType, parameters: TRequest, companySlug: string): Promise<TResponse> {
  const request: base.IRequest = {
    url: base.aiURL(CONTROLLER, 'execute'),
    method: 'POST',
    body: JSON.stringify({
      agentType,
      companySlug,
      parameters,
    }),
  };
  const response: base.IResponse<TResponse> = await base.requestWithRetries(request);

  if (response.error) {
    throw new Error(response.error.detail || 'Failed to execute agent request');
  }

  return response.data!;
}

/**
 * Executes an agent request with dynamic parameters
 * @param context Overmind context
 * @param payload The type of agent to execute, dynamic parameters for the agent request and company slug
 * @returns The agent response as any
 */
export async function executeDynamicAgentRequest(context: Context, payload: { agentType: string; parameters: any; companySlug: string }): Promise<any> {
  const request: base.IRequest = {
    url: base.aiURL(CONTROLLER, 'execute'),
    method: 'POST',
    body: JSON.stringify(payload),
  };
  const response: base.IResponse<any> = await base.requestWithRetries(request);

  if (response.error) {
    throw new Error(response.error.detail || 'Failed to execute agent request');
  }

  return response.data;
}

/**
 * Gets the JSON schema for a specific agent type
 * @param context Overmind context
 * @param payload The type of agent to get the schema for
 * @returns The agent schema
 */
export async function getAgentSchema(context: Context, payload: string): Promise<AgentSchema> {
  const request: base.IRequest = { url: base.aiURL(CONTROLLER, 'schema', payload) };
  const response: base.IResponse<AgentSchema> = await base.requestWithRetries(request);

  if (response.error) {
    throw new Error(response.error.detail || 'Failed to get agent schema');
  }

  return response.data!;
}
